import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Play, RefreshCw, Wand2 } from 'lucide-react';
import { useAppSelector } from '@/app/hooks';
import { RootState } from '@/app/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useTranslationLocales } from '@/hooks/useTranslationLocales';
import { configureSupportedLanguages } from '@/i18n';
import {
  fetchPaperTranslations,
  fetchPrinterTranslations,
  updatePaperTranslationManual,
  updatePrinterTranslationManual,
  triggerTranslationReconciliation,
  triggerTranslationWorkerRun,
  AdminPrinterTranslation,
  TranslationReconciliationSummary,
  TranslationWorkerSummary,
} from '@/services/adminService';
import { useAdminTranslations, buildFormKey, EntityType, TranslationEntity } from '@/hooks/useAdminTranslations';
import TranslationCard from '@/components/admin/TranslationCard';



const isPrinterEntity = (entity: TranslationEntity): entity is AdminPrinterTranslation => {
  return (entity as AdminPrinterTranslation).model !== undefined;
};

const AdminTranslationsPage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const { toast } = useToast();
  const { state, dispatch, handleDescriptionChange, handleStatusChange, handleReset } = useAdminTranslations();
  const { isLoading, loadError, paperTranslations, printerTranslations, paperFormState, printerFormState, dirtyKeys, savingKeys } = state;

  const [activeTab, setActiveTab] = useState<EntityType>('paper');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLocaleFilter, setActiveLocaleFilter] = useState<string>('all');
  const [isReconciling, setIsReconciling] = useState(false);
  const [isWorkerRunning, setIsWorkerRunning] = useState(false);
  const [lastReconcileSummary, setLastReconcileSummary] = useState<TranslationReconciliationSummary | null>(null);
  const [lastWorkerSummary, setLastWorkerSummary] = useState<TranslationWorkerSummary | null>(null);
  const { data: localeConfig, labelsByCode } = useTranslationLocales();

  useEffect(() => {
    if (localeConfig) {
      configureSupportedLanguages(localeConfig.all);
    }
  }, [localeConfig]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
    }
  }, [isAdmin, navigate]);

  const loadTranslations = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const [papers, printers] = await Promise.all([fetchPaperTranslations(), fetchPrinterTranslations()]);
      dispatch({ type: 'FETCH_SUCCESS', payload: { papers: [...papers], printers: [...printers] } });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load translations.';
      dispatch({ type: 'FETCH_ERROR', payload: message });
      toast({ title: 'Failed to load translations', description: message, variant: 'destructive' });
    }
  }, [dispatch, toast]);

  useEffect(() => {
    if (isAdmin) {
      void loadTranslations();
    }
  }, [isAdmin, loadTranslations]);

  const supportedLocales = useMemo(() => {
    if (!localeConfig) {
      return [] as Array<{ code: string; label: string }>;
    }
    return localeConfig.supported.map((entry) => ({
      code: entry.code,
      label: entry.label || entry.code.toUpperCase(),
    }));
  }, [localeConfig]);

  const getLocaleLabel = useCallback(
    (locale: string): string => labelsByCode?.[locale] ?? locale.toUpperCase(),
    [labelsByCode],
  );


  const handleCopyEnglish = useCallback(
    (entityType: EntityType, entityId: string, locale: string, englishDescription: string) => {
      handleDescriptionChange(entityType, entityId, locale, englishDescription);
    },
    [handleDescriptionChange],
  );

  const handleSaveTranslation = useCallback(async (entityType: EntityType, entityId: string, locale: string) => {
    const key = buildFormKey(entityType, entityId, locale);
    const formState = entityType === 'paper' ? paperFormState[key] : printerFormState[key];

    if (!formState) return;

    dispatch({ type: 'SET_SAVING', payload: { key, isSaving: true } });
    try {
      const result =
        entityType === 'paper'
          ? await updatePaperTranslationManual(entityId, locale, formState)
          : await updatePrinterTranslationManual(entityId, locale, formState);

      dispatch({ type: 'APPLY_UPDATE', payload: { entityType, entityId, locale, translation: { ...result.translation, locale: result.locale } } });

      toast({
        title: 'Translation saved',
        description: `${locale.toUpperCase()} translation for ${entityType} updated.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save translation.';
      toast({ title: 'Save failed', description: message, variant: 'destructive' });
    } finally {
      dispatch({ type: 'SET_SAVING', payload: { key, isSaving: false } });
    }
  }, [dispatch, paperFormState, printerFormState, toast]);

  const handleRunReconciliation = async () => {
    try {
      setIsReconciling(true);
      const summary = await triggerTranslationReconciliation();
      setLastReconcileSummary(summary);
      toast({
        title: 'Reconciliation queued',
        description: `Processed ${summary.itemsProcessed} sources · ${summary.tasksCreated} task(s) created.`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run reconciliation.';
      toast({ title: 'Reconciliation failed', description: message, variant: 'destructive' });
    } finally {
      setIsReconciling(false);
    }
  };

  const handleRunWorker = async () => {
    try {
      setIsWorkerRunning(true);
      const summary = await triggerTranslationWorkerRun();
      setLastWorkerSummary(summary);
      toast({
        title: 'Worker complete',
        description: summary.alreadyRunning
          ? 'Worker is already processing tasks on the server.'
          : `Processed ${summary.tasksAttempted} task(s) across ${summary.batchesProcessed} batch(es).`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run worker.';
      toast({ title: 'Worker failed', description: message, variant: 'destructive' });
    } finally {
      setIsWorkerRunning(false);
    }
  };

  const currentEntities = activeTab === 'paper' ? paperTranslations : printerTranslations;
  const currentFormState = activeTab === 'paper' ? paperFormState : printerFormState;

  const filteredEntities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const byLocale = activeLocaleFilter === 'all' ? null : activeLocaleFilter;

    return currentEntities.filter((entity) => {
      const matchesSearch = term
        ? [
            entity.id,
            entity.name,
            entity.brand,
            isPrinterEntity(entity) ? entity.model : '',
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term)) ||
          entity.englishDescription.toLowerCase().includes(term)
        : true;

      if (!matchesSearch) {
        return false;
      }

      if (!byLocale) {
        return true;
      }

      return entity.translations.some((translation) => translation.locale === byLocale);
    });
  }, [activeLocaleFilter, currentEntities, searchTerm]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-8">
      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-2xl">Translation control center</CardTitle>
              <CardDescription>
                Trigger reconciliation and worker cycles, then review paper and printer translations.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => void loadTranslations()} disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Reload data
              </Button>
              <Button onClick={() => void handleRunReconciliation()} disabled={isReconciling}>
                {isReconciling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                Run reconciliation
              </Button>
              <Button variant="secondary" onClick={() => void handleRunWorker()} disabled={isWorkerRunning}>
                {isWorkerRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Run worker cycle
              </Button>
            </div>
          </CardHeader>
          {(lastReconcileSummary || lastWorkerSummary) && (
            <CardFooter className="flex flex-col items-start gap-2 border-t border-border/60 bg-muted/40 p-6 text-sm text-muted-foreground">
              {lastReconcileSummary && (
                <div>
                  <strong className="text-foreground">Last reconciliation</strong> · Processed{' '}
                  {lastReconcileSummary.itemsProcessed} sources · {lastReconcileSummary.tasksCreated} task(s) created
                </div>
              )}
              {lastWorkerSummary && (
                <div>
                  <strong className="text-foreground">Last worker run</strong> ·{' '}
                  {lastWorkerSummary.alreadyRunning
                    ? 'Worker already running on the server.'
                    : `${lastWorkerSummary.tasksAttempted} task(s) attempted across ${lastWorkerSummary.batchesProcessed} batch(es)`}
                </div>
              )}
            </CardFooter>
          )}
        </Card>

        {loadError && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-destructive">Failed to load translations</CardTitle>
              <CardDescription className="text-destructive/80">{loadError}</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" onClick={() => void loadTranslations()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </CardFooter>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Translation inventory</CardTitle>
            <CardDescription>
              Review and curate localized descriptions for paper types and printers. Use search and locale filters to
              narrow the list.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="inline-flex gap-2 rounded-md bg-muted p-1">
                <Button
                  variant={activeTab === 'paper' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('paper')}
                >
                  Paper types
                </Button>
                <Button
                  variant={activeTab === 'printer' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('printer')}
                >
                  Printers
                </Button>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={`Search ${activeTab === 'paper' ? 'paper types' : 'printers'}…`}
                  className="w-full sm:w-64"
                />
                <Select value={activeLocaleFilter} onValueChange={setActiveLocaleFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Locale" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All locales</SelectItem>
                    {supportedLocales.map((locale) => (
                      <SelectItem key={locale.code} value={locale.code}>
                        {locale.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loading translations…
              </div>
            ) : filteredEntities.length === 0 ? (
              <div className="rounded-lg border border-dashed border-muted-foreground/40 p-10 text-center text-muted-foreground">
                No translations match your filters yet.
              </div>
            ) : (
              <ScrollArea className="h-[70vh] rounded-md border">
                <div className="space-y-6 p-6">
                  {filteredEntities.map((entity) => (
                    <Card key={entity.id} className="border-muted-foreground/20 shadow-none">
                      <CardHeader className="gap-3">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <CardTitle className="text-lg">
                              {activeTab === 'paper'
                                ? `${entity.name} · ${entity.brand}`
                                : `${entity.name} · ${entity.brand} ${isPrinterEntity(entity) ? entity.model : ''}`}
                            </CardTitle>
                            <CardDescription className="max-w-2xl">
                              {entity.englishDescription || 'No English description available.'}
                            </CardDescription>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            <div>Entity ID: {entity.id}</div>
                            <div className="font-mono text-[11px]">Hash: {entity.englishHash}</div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="rounded-lg border border-muted p-4">
                          <Label className="mb-2 block text-xs uppercase tracking-wide text-muted-foreground">
                            English reference
                          </Label>
                          <p className="whitespace-pre-wrap text-sm text-foreground/80">
                            {entity.englishDescription || '—'}
                          </p>
                        </div>

                        {entity.translations
                          .filter((translation) =>
                            activeLocaleFilter === 'all' ? true : translation.locale === activeLocaleFilter,
                          )
                          .map((translation) => {
                            const key = buildFormKey(activeTab, entity.id, translation.locale);
                            const formValue = currentFormState[key] ?? {
                              description: translation.description ?? '',
                              status: translation.status ?? 'mt_seeded',
                            };
                            return (
                              <TranslationCard
                                key={translation.locale}
                                entityType={activeTab}
                                entityId={entity.id}
                                englishDescription={entity.englishDescription}
                                translation={translation}
                                formValue={formValue}
                                isDirty={Boolean(dirtyKeys[key])}
                                isSaving={Boolean(savingKeys[key])}
                                getLocaleLabel={getLocaleLabel}
                                onDescriptionChange={(value) => handleDescriptionChange(activeTab, entity.id, translation.locale, value)}
                                onStatusChange={(value) => handleStatusChange(activeTab, entity.id, translation.locale, value)}
                                onSave={() => void handleSaveTranslation(activeTab, entity.id, translation.locale)}
                                onReset={() => handleReset(activeTab, entity.id, translation.locale)}
                                onCopyEnglish={() => handleCopyEnglish(activeTab, entity.id, translation.locale, entity.englishDescription)}
                              />
                            );
                          })}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminTranslationsPage;
